import React from 'react';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { useConsent } from './ConsentContext';

const Banner = styled.div`
  position: fixed;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.blue};
  color: ${({ theme }) => theme.colors.white};
  padding: 1rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-size: 0.9rem;
  line-height: 1.4;

  ${breakpoint('md')`
    left: auto;
    right: 1.5rem;
    bottom: 1.5rem;
    max-width: 420px;
  `};

  /* The preferences panel carries four actions, which will not fit the width
     the two- and three-button variants are sized for. */
  &[data-wide='true'] {
    ${breakpoint('md')`
      max-width: 560px;
    `};
  }

  a {
    color: ${({ theme }) => theme.colors.orange};
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Button = styled.button<{ primary?: boolean }>`
  appearance: none;
  border: 1px solid ${({ theme, primary }) => (primary ? theme.colors.orange : 'rgba(255,255,255,0.4)')};
  background: ${({ theme, primary }) => (primary ? theme.colors.orange : 'transparent')};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.45rem 0.9rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const CookieConsent: React.FC = () => {
  const {
    isHydrated,
    isConsentRequired,
    isGpcEnabled,
    hasExplicitChoice,
    isPreferencesOpen,
    openPreferences,
    closePreferences,
    chooseEssential,
    chooseAnalytics,
    chooseMarketing,
  } = useConsent();
  const panelRef = React.useRef<HTMLDivElement>(null);

  // The panel is opened deliberately from the footer, so move focus into it.
  // Focus is not trapped — the banner never blocks the page, and trapping it
  // would strand keyboard users who only wanted to keep reading.
  React.useEffect(() => {
    if (isPreferencesOpen) panelRef.current?.focus();
  }, [isPreferencesOpen]);

  // Nothing is rendered server-side: the country cookie and any stored choice
  // are only readable on the client, and rendering the banner before reading
  // them would flash it at every visitor who does not need to see it.
  if (!isHydrated) return null;

  // Reopened from the footer link — the full three-level choice, everywhere.
  if (isPreferencesOpen) {
    return (
      <Banner ref={panelRef} tabIndex={-1} role="region" aria-label="Privacy preferences" data-wide="true">
        <div>
          Choose which cookies we may store on your device. Essential cookies keep the site working. If you decline
          analytics, we still measure visits anonymously, without storing anything on your device. See our{' '}
          <a href="/privacy">Privacy Policy</a> for details.
        </div>
        <Actions>
          <Button onClick={closePreferences} type="button">
            Close
          </Button>
          <Button onClick={chooseEssential} type="button">
            Essential only
          </Button>
          <Button onClick={chooseAnalytics} type="button">
            Analytics only
          </Button>
          <Button onClick={chooseMarketing} type="button" primary>
            Accept all
          </Button>
        </Actions>
      </Banner>
    );
  }

  // A visitor who already chose, or who opted out at the browser level, is done.
  if (hasExplicitChoice || isGpcEnabled) return null;

  // Opt-out jurisdictions: cookies are already allowed, so this is a notice
  // rather than a gate. Offering a prominent "decline" here would cost
  // measurement without buying compliance.
  if (!isConsentRequired) {
    return (
      <Banner role="region" aria-label="Cookie notice">
        <div>
          We use cookies to help improve your experience and understand how you use our website. You can change your
          cookie preferences at any time. See our <a href="/privacy">Privacy Policy</a>.
        </div>
        <Actions>
          <Button onClick={openPreferences} type="button">
            Manage preferences
          </Button>
          <Button onClick={chooseMarketing} type="button" primary>
            Got it
          </Button>
        </Actions>
      </Banner>
    );
  }

  return (
    <Banner role="region" aria-label="Cookie consent">
      <div>
        We use cookies to help improve your experience and understand how you use our website. You can change your
        cookie preferences at any time. See our <a href="/privacy">Privacy Policy</a>.
      </div>
      <Actions>
        <Button onClick={chooseEssential} type="button">
          Essential only
        </Button>
        <Button onClick={chooseAnalytics} type="button">
          Analytics only
        </Button>
        <Button onClick={chooseMarketing} type="button" primary>
          Accept all
        </Button>
      </Actions>
    </Banner>
  );
};

export default CookieConsent;
